import { z } from "zod/v4";

import {
  AgenticLogicDatasetDtoDescriptions,
  AgenticLogicDatasetListItemDtoDescriptions,
  AgenticLogicDatasetReadDescriptions,
  AgenticLogicDatasetRevisionStampDescriptions,
  AgenticLogicDatasetSummaryDtoDescriptions,
  AgenticLogicDependentRuleDescriptions,
} from "../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../descriptions/types";

// ---------------------------------------------------------------------------
// Datasets — the generic, org-scoped dataset registry behind
// sdk.getDataset(key). The agent owns this data: it authors a dataset while
// creating a rule (key + description + JSON Schema + payload) and searches
// the registry for reuse before creating something new. There are no fixed
// namespaces — the shapes that used to be hardcoded survive only as clonable
// templates (see functions/agentic-logic/datasets/templates.ts).
//
// Contract properties:
//  - Each dataset DECLARES its own shape as data: a JSON Schema stored next
//    to the payload. Writes are validated against it (ajv); reads never
//    re-validate.
//  - ONE row per dataset, updated in place. Every write bumps `revision`, a
//    monotonic etag the read carries so caches and telemetry can tell two
//    payloads apart. It is not history: superseded payloads are not kept.
//  - Reads return plain serializable snapshots, so the SDK can memoize one
//    per invocation (pinned per invocation) and record which revision it saw.
//  - An unknown key is a typed error, never an invented empty payload: a
//    generic store cannot know the canonical empty for a shape it has never
//    been told about.
//  - A dataset a rule's ACTIVE version reads cannot be deleted (a paused
//    rule keeps its active version, so it blocks too), and its schema can
//    only change in ways the new payload still satisfies: the previous
//    stored schema (every reader's contract, one write deep) AND the schema
//    each active version PINNED at publish (`datasetSchemas` in its read
//    manifest — its contract however many writes ago). A version published
//    before pins existed has none and is covered by the previous stored
//    schema alone. Both refusals are a typed 409 naming the dependent rules.
// ---------------------------------------------------------------------------

/**
 * Syntactic shape of a dataset key (lower-kebab, agent-chosen, stable): rule
 * code calls `sdk.getDataset(key)` with these strings forever, so keys are
 * never renamed once rules ship against them.
 */
export const AGENTIC_LOGIC_DATASET_KEY_PATTERN = /^[a-z][a-z0-9-]*$/;
export const AGENTIC_LOGIC_DATASET_KEY_MAX_LENGTH = 128;
export const AGENTIC_LOGIC_DATASET_NAMESPACE_MAX_LENGTH = 64;

/** Hard payload cap, enforced loudly at write time — never truncated. */
export const AGENTIC_LOGIC_DATASET_MAX_PAYLOAD_BYTES = 1_048_576;

/**
 * Caps on the declared schema (serialized bytes) and the description. The
 * schema is compiled on every write and shipped in every listing, and the
 * description is what reuse search reads — neither is a place for bulk data.
 */
export const AGENTIC_LOGIC_DATASET_MAX_JSON_SCHEMA_BYTES = 65_536;
export const AGENTIC_LOGIC_DATASET_DESCRIPTION_MAX_LENGTH = 2048;

export const AgenticLogicDatasetKeySchema = z
  .string()
  .max(AGENTIC_LOGIC_DATASET_KEY_MAX_LENGTH)
  .regex(
    AGENTIC_LOGIC_DATASET_KEY_PATTERN,
    "Dataset keys are lower-kebab: a lowercase letter followed by lowercase letters, digits, or hyphens"
  );
export type AgenticLogicDatasetKey = z.infer<
  typeof AgenticLogicDatasetKeySchema
>;

/**
 * Coarse grouping for list filtering only — it carries no schema semantics.
 * Same lower-kebab syntax as keys.
 */
export const AgenticLogicDatasetNamespaceSchema = z
  .string()
  .max(AGENTIC_LOGIC_DATASET_NAMESPACE_MAX_LENGTH)
  .regex(
    AGENTIC_LOGIC_DATASET_KEY_PATTERN,
    "Dataset namespaces are lower-kebab: a lowercase letter followed by lowercase letters, digits, or hyphens"
  );

/**
 * The declared shape, as data. Structurally loose at the boundary (any JSON
 * object); the service compiles it with ajv before any write, so a malformed
 * schema is a 400, never a stored row.
 */
export const AgenticLogicDatasetJsonSchemaSchema = z.record(
  z.string(),
  z.unknown()
);

/** The monotonic etag every write bumps (see the contract above). */
export const AgenticLogicDatasetRevisionSchema = z.number().int().positive();

const AgenticLogicDatasetSummaryShape = {
  key: AgenticLogicDatasetKeySchema.describe(
    AgenticLogicDatasetSummaryDtoDescriptions.key
  ),
  namespace: AgenticLogicDatasetNamespaceSchema.describe(
    AgenticLogicDatasetSummaryDtoDescriptions.namespace
  ),
  description: z
    .string()
    .min(1)
    .max(AGENTIC_LOGIC_DATASET_DESCRIPTION_MAX_LENGTH)
    .describe(AgenticLogicDatasetSummaryDtoDescriptions.description),
  jsonSchema: AgenticLogicDatasetJsonSchemaSchema.describe(
    AgenticLogicDatasetSummaryDtoDescriptions.jsonSchema
  ),
  revision: AgenticLogicDatasetRevisionSchema.describe(
    AgenticLogicDatasetSummaryDtoDescriptions.revision
  ),
  entryCount: z
    .number()
    .int()
    .nonnegative()
    .describe(AgenticLogicDatasetSummaryDtoDescriptions.entryCount),
  payloadBytes: z
    .number()
    .int()
    .nonnegative()
    .describe(AgenticLogicDatasetSummaryDtoDescriptions.payloadBytes),
  lastUpdateTs: z
    .number()
    .describe(AgenticLogicDatasetSummaryDtoDescriptions.lastUpdateTs),
};

/**
 * The discovery shape: everything an agent needs to judge reuse (description
 * + declared schema + size signals) and NOTHING more — it deliberately
 * carries no payload, so listing every dataset stays cheap at any org size.
 */
export const AgenticLogicDatasetSummaryDtoSchema = z
  .object(AgenticLogicDatasetSummaryShape)
  .describe(AgenticLogicDatasetSummaryDtoDescriptions._schema);
export type AgenticLogicDatasetSummaryDto = z.infer<
  typeof AgenticLogicDatasetSummaryDtoSchema
>;

export const AgenticLogicDatasetDtoSchema = z
  .object({
    ...AgenticLogicDatasetSummaryShape,
    payload: z.json().describe(AgenticLogicDatasetDtoDescriptions.payload),
  })
  .describe(AgenticLogicDatasetDtoDescriptions._schema);
export type AgenticLogicDatasetDto = z.infer<
  typeof AgenticLogicDatasetDtoSchema
>;

/**
 * One published rule that reads a dataset or Source. Delete and
 * incompatible-schema refusals list these, and the listings show them as
 * "used by".
 */
export const AgenticLogicDependentRuleSchema = z
  .object({
    ruleId: z.string().describe(AgenticLogicDependentRuleDescriptions.ruleId),
    ruleName: z
      .string()
      .describe(AgenticLogicDependentRuleDescriptions.ruleName),
  })
  .describe(AgenticLogicDependentRuleDescriptions._schema);
export type AgenticLogicDependentRule = z.infer<
  typeof AgenticLogicDependentRuleSchema
>;

/** The listing row: the discovery summary plus the rules that read it. */
export const AgenticLogicDatasetListItemDtoSchema = z
  .object({
    ...AgenticLogicDatasetSummaryShape,
    usedBy: z
      .array(AgenticLogicDependentRuleSchema)
      .describe(AgenticLogicDatasetListItemDtoDescriptions.usedBy),
  })
  .describe(AgenticLogicDatasetListItemDtoDescriptions._schema);
export type AgenticLogicDatasetListItemDto = z.infer<
  typeof AgenticLogicDatasetListItemDtoSchema
>;

/**
 * What a runtime dataset read returns: the payload plus the revision that
 * produced it, so the evaluation's telemetry can record what actually ran.
 */
export const AgenticLogicDatasetReadSchema = z
  .object({
    key: AgenticLogicDatasetKeySchema.describe(
      AgenticLogicDatasetReadDescriptions.key
    ),
    revision: AgenticLogicDatasetRevisionSchema.describe(
      AgenticLogicDatasetReadDescriptions.revision
    ),
    // DEPRECATED — always equal to `revision`. Rules published before the
    // rename bundle an SDK whose read schema REQUIRES `version` (a plain,
    // non-strict z.object, so the extra `revision` is stripped, not
    // rejected). The serving read keeps sending it until no published bundle
    // predates the rename; new code reads `revision`.
    version: AgenticLogicDatasetRevisionSchema.optional().describe(
      AgenticLogicDatasetReadDescriptions.version
    ),
    payload: z.json().describe(AgenticLogicDatasetReadDescriptions.payload),
  })
  .describe(AgenticLogicDatasetReadDescriptions._schema);
export type AgenticLogicDatasetRead = z.infer<
  typeof AgenticLogicDatasetReadSchema
>;

/**
 * One resolved dataset pin of an evaluation — the telemetry stamp that
 * records which dataset revisions actually produced a result.
 */
export const AgenticLogicDatasetRevisionStampSchema = z
  .object({
    key: AgenticLogicDatasetKeySchema.describe(
      AgenticLogicDatasetRevisionStampDescriptions.key
    ),
    revision: AgenticLogicDatasetRevisionSchema.describe(
      AgenticLogicDatasetRevisionStampDescriptions.revision
    ),
  })
  .describe(AgenticLogicDatasetRevisionStampDescriptions._schema);
export type AgenticLogicDatasetRevisionStamp = z.infer<
  typeof AgenticLogicDatasetRevisionStampSchema
>;

/**
 * The `data` of a dataset write or delete refused with a 409. The reason is
 * the discriminator a client branches on:
 *  - `revision_mismatch`: the PUT carried `ifRevision` and the stored row is
 *    at another revision (`currentRevision`), or was deleted (null).
 *  - `has_dependents`: a DELETE while a rule's active version reads the key.
 *  - `incompatible_schema`: a PUT that changes the schema of a key active
 *    versions read, with a payload the previous stored schema or one of
 *    their pinned schemas rejects (`detail`); `dependents` names exactly the
 *    rules whose contract the payload fails.
 */
export const AgenticLogicDatasetConflictSchema = z.discriminatedUnion(
  "reason",
  [
    z.object({
      reason: z.literal("revision_mismatch"),
      key: AgenticLogicDatasetKeySchema,
      currentRevision: AgenticLogicDatasetRevisionSchema.nullable(),
    }),
    z.object({
      reason: z.literal("has_dependents"),
      key: AgenticLogicDatasetKeySchema,
      dependents: z.array(AgenticLogicDependentRuleSchema),
    }),
    z.object({
      reason: z.literal("incompatible_schema"),
      key: AgenticLogicDatasetKeySchema,
      dependents: z.array(AgenticLogicDependentRuleSchema),
      detail: z.string(),
    }),
  ]
);
export type AgenticLogicDatasetConflict = z.infer<
  typeof AgenticLogicDatasetConflictSchema
>;

// ---------------------------------------------------------------------------
// Compile-time checks: description objects must cover all schema keys
// ---------------------------------------------------------------------------

assertDescriptions<
  typeof AgenticLogicDatasetSummaryDtoDescriptions,
  AgenticLogicDatasetSummaryDto
>();
assertDescriptions<
  typeof AgenticLogicDatasetDtoDescriptions,
  AgenticLogicDatasetDto
>();
assertDescriptions<
  typeof AgenticLogicDatasetReadDescriptions,
  AgenticLogicDatasetRead
>();
assertDescriptions<
  typeof AgenticLogicDatasetRevisionStampDescriptions,
  AgenticLogicDatasetRevisionStamp
>();
assertDescriptions<
  typeof AgenticLogicDependentRuleDescriptions,
  AgenticLogicDependentRule
>();
assertDescriptions<
  typeof AgenticLogicDatasetListItemDtoDescriptions,
  AgenticLogicDatasetListItemDto
>();
